/**
 * monitor-templates.ts — ready-made monitor "recipes" for the create wizard.
 *
 * Each template is a richer {@link WatchpointPrefillState} (type + config + schedule)
 * grouped by intent, so users start from a goal instead of a blank form. The gallery
 * on step 0 of WatchpointCreate applies one, seeding the form and jumping to Configure.
 *
 * All configs use ONLY fields the backend actually supports per type (see the alarm
 * field catalog / probe executors). Placeholder targets (example.com, blank
 * customer_id) are meant to be edited before creating — nothing is persisted until submit.
 *
 * `alarm` is an optional starter-alarm seed surfaced after the monitor is created
 * (wired in a later phase); conditions use catalog-valid fields/operators, with
 * `$sigma`/`$sigma_abs` only on baseline-backed metrics (count / latency_ms / derived).
 */
import type { WatchpointPrefillState } from '../onboarding/sample-monitor';
import type { ConditionRow } from '../alarms/condition-serializer';
import type { AlarmSeverity } from '@/api/healthcheck';

export interface AlarmSeed {
  name: string;
  severity: AlarmSeverity;
  rows: ConditionRow[];
}

export type TemplateCategory =
  | 'Availability'
  | 'Certificate'
  | 'Traffic & Security';

export interface MonitorTemplate {
  id: string;
  label: string;
  description: string;
  category: TemplateCategory;
  /** Monitor form prefill (type + config + schedule). Reuses the wizard's prefill. */
  monitor: WatchpointPrefillState;
  /** Optional starter alarm seeded after the monitor is created. */
  alarm?: AlarmSeed;
}

const s = (k: string, operator: string, value: unknown): ConditionRow => ({ field: k, operator, value });

export const MONITOR_TEMPLATES: MonitorTemplate[] = [
  // ---- Availability -------------------------------------------------------
  {
    id: 'website-uptime',
    label: 'Website uptime',
    description: 'HTTPS endpoint returns 200; alerts on downtime + latency anomaly.',
    category: 'Availability',
    monitor: { name: 'Website uptime', type: 'https', config: { url: 'https://example.com', method: 'GET', expected_status: 200 }, startStep: 1 },
    alarm: { name: 'Website down', severity: 'critical', rows: [s('is_up', '$eq', false)] },
  },
  {
    id: 'api-health',
    label: 'API endpoint health',
    description: 'Checks status 200 and a body marker; alerts on 5xx.',
    category: 'Availability',
    monitor: { name: 'API health', type: 'https', config: { url: 'https://api.example.com/health', method: 'GET', expected_status: 200, expected_body_contains: 'ok' }, startStep: 1 },
    alarm: { name: 'API 5xx', severity: 'critical', rows: [s('status_code', '$gte', 500)] },
  },
  {
    id: 'tcp-port',
    label: 'TCP port reachable',
    description: 'Verifies a host:port accepts connections (DB, cache, custom service).',
    category: 'Availability',
    monitor: { name: 'TCP port', type: 'tcp', config: { host: 'db.example.com', port: 5432 }, startStep: 1 },
    alarm: { name: 'Port unreachable', severity: 'critical', rows: [s('is_up', '$eq', false)] },
  },
  {
    id: 'dns-resolve',
    label: 'DNS resolves',
    description: 'Resolves a record and (optionally) checks its expected values.',
    category: 'Availability',
    monitor: { name: 'DNS resolution', type: 'dns', config: { host: 'example.com', record_type: 'A' }, startStep: 1 },
    alarm: { name: 'DNS resolution failed', severity: 'critical', rows: [s('is_up', '$eq', false)] },
  },

  // ---- Certificate --------------------------------------------------------
  {
    id: 'tls-cert-expiry',
    label: 'TLS certificate expiry',
    description: 'Warns before a certificate expires (30-day threshold).',
    category: 'Certificate',
    monitor: { name: 'TLS certificate', type: 'tls', config: { host: 'example.com', port: 443, expiry_threshold_days: 30, verify_hostname: true }, startStep: 1 },
    alarm: { name: 'Cert expiring soon', severity: 'warning', rows: [s('days_until_expiry', '$lte', 14)] },
  },

  // ---- Traffic & Security (elastic WAF logs) ------------------------------
  {
    id: 'elastic-5xx-surge',
    label: '5xx error surge',
    description: 'Per-URI 5xx counts; fires on an anomalous spike (σ, self-tuning).',
    category: 'Traffic & Security',
    monitor: { name: '5xx surge', type: 'elastic', config: { query: '*', domains: ['www.example.com'], dimensions: ['uri'], metric: 'count', filters: { status: [500, 502, 503, 504] } }, startStep: 1 },
    alarm: { name: '5xx spike (σ)', severity: 'warning', rows: [s('count', '$sigma', 3)] },
  },
  {
    id: 'elastic-single-ip-flood',
    label: 'Single-IP flood (DDoS)',
    description: 'Busiest client IP; fires when one source spikes (top1 σ).',
    category: 'Traffic & Security',
    monitor: { name: 'Single-IP flood', type: 'elastic', config: { query: '*', domains: ['www.example.com'], dimensions: ['remote_ip'], metric: 'count' }, startStep: 1 },
    alarm: { name: 'Single-IP spike (σ)', severity: 'warning', rows: [s('derived:top1_count', '$sigma', 3)] },
  },
  {
    id: 'elastic-distributed-scan',
    label: 'Distributed scan',
    description: 'Number of distinct URIs hit; fires when it spreads (distinct σ).',
    category: 'Traffic & Security',
    monitor: { name: 'Distributed scan', type: 'elastic', config: { query: '*', domains: ['www.example.com'], dimensions: ['uri'], metric: 'count' }, startStep: 1 },
    alarm: { name: 'Scan spread (σ)', severity: 'warning', rows: [s('derived:distinct_count', '$sigma', 3)] },
  },
  {
    id: 'elastic-waf-blocks',
    label: 'WAF rule blocks',
    description: 'Requests blocked by a WAF rule (mitigation = WAFRULE).',
    category: 'Traffic & Security',
    monitor: { name: 'WAF rule blocks', type: 'elastic', config: { query: '*', domains: ['www.example.com'], dimensions: ['mitigation'], metric: 'count' }, startStep: 1 },
    alarm: { name: 'WAF block', severity: 'critical', rows: [s('mitigation_category', '$eq', 'WAFRULE'), s('count', '$gte', 1)] },
  },

  // ---- Traffic & Security (BI — timeseries σ, self-tuning) ---------------
  {
    id: 'waap-threats-trend',
    label: 'WAAP threats trend (σ)',
    description: 'Per-minute threats by risk level; fires on an anomalous surge (σ, self-tuning).',
    category: 'Traffic & Security',
    // Timeseries metric → exclusive sole metric; 5-min cadence (BI fetch is heavy).
    monitor: { name: 'WAAP threats trend', type: 'waap', config: { customer_id: '', metrics: ['threats_trend'] }, check_interval: 300, startStep: 1 },
    alarm: { name: 'Threat surge (σ)', severity: 'warning', rows: [s('count', '$sigma', 2)] },
  },
  {
    id: 'waap-high-risk-threshold',
    label: 'High-risk threats (threshold)',
    description: 'AI-WAF high-risk count over the window; simple fixed threshold.',
    category: 'Traffic & Security',
    monitor: { name: 'High-risk threats', type: 'waap', config: { customer_id: '', metrics: ['high_risk'] }, check_interval: 3600, startStep: 1 },
    alarm: { name: 'High-risk threats', severity: 'warning', rows: [s('high_risk', '$gte', 100)] },
  },
  {
    id: 'delivery-status-anomaly',
    label: 'HTTP status anomaly (σ)',
    description: 'Per-minute requests by HTTP status; fires when a status code spikes (σ).',
    category: 'Traffic & Security',
    monitor: { name: 'HTTP status anomaly', type: 'delivery', config: { customer_id: '', metrics: ['http_status_ts'] }, check_interval: 300, startStep: 1 },
    alarm: { name: 'Status spike (σ)', severity: 'warning', rows: [s('count', '$sigma', 3)] },
  },
  {
    id: 'delivery-volume-drop',
    label: 'CDN volume drop (σ)',
    description: 'Per-minute CDN delivery volume; fires on an anomalous drop (σ−, self-tuning).',
    category: 'Traffic & Security',
    monitor: { name: 'CDN volume drop', type: 'delivery', config: { customer_id: '', metrics: ['delivery_volume'] }, check_interval: 300, startStep: 1 },
    alarm: { name: 'Volume drop (σ−)', severity: 'warning', rows: [s('count', '$sigma_drop', 2)] },
  },
];

/** Templates grouped by category, in display order. */
export const TEMPLATE_CATEGORIES: TemplateCategory[] = [
  'Availability',
  'Certificate',
  'Traffic & Security',
];
